import { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import { Camera, X } from 'lucide-react';

// Simple component for camera selection with minimal overhead

interface CameraSelectorProps {
  onSelect: (deviceId: string) => void;
  onClose: () => void;
}

export const CameraSelector = ({ onSelect, onClose }: CameraSelectorProps) => {
  const { t } = useTranslation();
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [resolutions, setResolutions] = useState<Record<string, string>>({});
  const [loadingCamera, setLoadingCamera] = useState<string | null>(null);

  // Efficiently enumerate available cameras with error handling
  const getAvailableCameras = useCallback(async () => {
    try {
      // First, request a temporary stream to obtain permissions and populate device labels
      const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
      tempStream.getTracks().forEach(track => track.stop());

      // Now enumerate devices with proper labels
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((d) => d.kind === 'videoinput');
      setAvailableCameras(cameras);
    } catch (e) {
      // Graceful fallback for permission issues
      setAvailableCameras([]);
    }
  }, []);

  useEffect(() => {
    getAvailableCameras();
  }, [getAvailableCameras]);

  // Probe each camera to obtain a resolution string like "640x480".
  // Strategy: initialize all known cameras to the explicit empty sentinel ('') so
  // the UI shows the localized "not available" and buttons are disabled.
  // Then probe each device; when a real resolution is found we overwrite the
  // sentinel and the UI will enable that device and show its resolution.
  useEffect(() => {
    let mounted = true;

    const probe = async (deviceId: string) => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: deviceId } } });
        try {
          const track = stream.getVideoTracks()[0];
          const settings = track.getSettings?.();
          let width = settings?.width;
          let height = settings?.height;

          if (!width || !height) {
            const video = document.createElement('video');
            video.style.position = 'fixed';
            video.style.left = '-9999px';
            video.style.width = '1px';
            video.style.height = '1px';
            video.autoplay = true;
            video.playsInline = true;
            video.muted = true;
            video.srcObject = stream;
            document.body.appendChild(video);
            await new Promise<void>((resolve) => {
              const onLoaded = () => {
                width = video.videoWidth || width;
                height = video.videoHeight || height;
                video.removeEventListener('loadedmetadata', onLoaded);
                resolve();
              };
              video.addEventListener('loadedmetadata', onLoaded);
              setTimeout(() => resolve(), 300);
            });
            try { document.body.removeChild(video); } catch {}
          }

          if (mounted) {
            if (width && height) {
              setResolutions(prev => ({ ...prev, [deviceId]: `${Math.round(width)}x${Math.round(height)}` }));
            } else {
              // keep the explicit empty sentinel if we couldn't determine size
              setResolutions(prev => ({ ...prev, [deviceId]: '' }));
            }
          }
        } finally {
          stream.getTracks().forEach(t => t.stop());
        }
      } catch (err) {
        if (mounted) setResolutions(prev => ({ ...prev, [deviceId]: '' }));
      }
    };

    // Initialize all known cameras to the empty sentinel so they appear as
    // (Not available) and disabled until a probe updates them.
    setResolutions(prev => {
      const next: Record<string, string> = { ...prev };
      availableCameras.forEach(cam => { next[cam.deviceId] = ''; });
      return next;
    });

    // Stagger probes to avoid hammering the system; overwrite sentinel when ready
    availableCameras.forEach((cam, index) => {
      setTimeout(() => { if (mounted) probe(cam.deviceId); }, index * 100);
    });

    return () => { mounted = false; };
  }, [availableCameras]);

  // Generate user-friendly camera names with fallback for unnamed devices
  const getCameraDisplayName = (camera: MediaDeviceInfo, index: number) => {
    if (camera.label) {
      // Try to identify camera type from label
      const label = camera.label.toLowerCase();
      if (label.includes('front') || label.includes('user') || label.includes('selfie')) {
        return t('camera1').replace('1', ` ${index + 1} (frontal)`);
      }
      if (label.includes('back') || label.includes('rear') || label.includes('environment')) {
        return t('camera1').replace('1', ` ${index + 1} (trasera)`);
      }
      return camera.label;
    }
    
    // Fallback names with position hints
    const names = [
      t('camera1').replace('1', ' 1 - frontal'),
      t('camera1').replace('1', ' 2 - trasera'),
      t('camera1').replace('1', ` ${index + 1}`)
    ];
    return names[index] || t('camera1').replace('1', ` ${index + 1}`);
  };

  return (
  <Card className="absolute z-50 left-0 right-0 p-7 bg-card border-elegant-border rounded-xl">
      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
        className="absolute top-2 right-2 h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground"
        aria-label={t('close')}
      >
        <X className="h-4 w-4" />
      </Button>

      <div className="space-y-4">
        <div>
          <h3 className="text-xl font-bold flex items-center" style={{ color: '#7d1b2d' }}>
            <Camera className="mr-2 h-6 w-6" style={{ color: '#7d1b2d' }} />
            {t('selectCamera')}
          </h3>
        </div>
  
        {availableCameras.length === 0 ? (
          <div className="text-center py-8">
            <Camera className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
              {t('noCamerasDetected')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {availableCameras.map((camera, index) => (
              <Button
                key={camera.deviceId || index}
                onClick={async () => {
                  const deviceId = camera.deviceId;
                  setLoadingCamera(deviceId);
                  
                  try {
                    // For mobile devices, don't rely on pre-probed resolutions
                    // Just attempt to select the camera directly
                    if (!resolutions[deviceId]) {
                      // Try a quick permission check with timeout
                      const timeoutPromise = new Promise((_, reject) => {
                        setTimeout(() => reject(new Error('Timeout')), 2000);
                      });
                      
                      try {
                        const stream = await Promise.race([
                          navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: deviceId } } }),
                          timeoutPromise
                        ]) as MediaStream;
                        stream.getTracks().forEach(track => track.stop());
                      } catch (permissionError) {
                        // If permission fails, still try to select the camera
                        // The parent component will handle the actual camera access
                        console.warn('Permission check failed for camera', deviceId, permissionError);
                      }
                    }
                    
                    onSelect(deviceId);
                  } catch (error) {
                    console.error('Error selecting camera', deviceId, error);
                  } finally {
                    setLoadingCamera(null);
                  }
                }}
                disabled={loadingCamera === camera.deviceId || resolutions[camera.deviceId] === ''}
                variant="highlighted"
                className="w-full justify-start text-left"
              >
                <Camera className="mr-2 h-4 w-4" />
                {getCameraDisplayName(camera, index)}
                {(() => {
                  const res = resolutions[camera.deviceId];
                  // If probing produced an explicit empty string, show localized 'not available' and disable
                  if (res === '') {
                    return ` (${t('notAvailable')})`;
                  }
                  return res ? ` (${res})` : '';
                })()}
                {loadingCamera === camera.deviceId && ' ...'}
              </Button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};